import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card } from '@/components/ui/Card';
import { ComplianceRequestContext, ConfigContext } from '@/context';
import { api } from '@/utils/api';
import { handleError } from '@/utils/error';
import { Button } from '@/components/ui/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  FormLabel,
} from '@/components/ui/Form';
import { Textarea } from '@/components/ui/Textarea';
import { Input } from '@/components/ui/Input';
import { useError } from '@/components/ui/useError';
import { Alert } from '@/components/ui/Alert';
import { Warning } from '@phosphor-icons/react';
import {
  object,
  pipe,
  string,
  nonEmpty,
  email,
  optional,
  union,
  regex,
  literal,
  InferInput,
} from 'valibot';
import { valibotResolver } from '@hookform/resolvers/valibot';

type Props = {
  detailsRequired?: boolean;
  requestType: string;
  title: string;
  description: string | JSX.Element;
  placeholder: string;
  onCancel: () => void;
  onSubmit: () => void;
};

export function ComplianceRequestCreate({
  detailsRequired,
  requestType,
  title,
  description,
  placeholder,
  onCancel,
  onSubmit,
}: Props) {
  const [loading, setLoading] = useState<boolean>(false);

  const { setComplianceRequest } = useContext(ComplianceRequestContext);
  const {
    consentSettingsCancelButton,
    consentSettingsSubmitButton,
    errorText,
    errorTitle,
    privacyRequestsRequestsFirstName,
    privacyRequestsRequestsLastName,
    privacyRequestsRequestsEmail,
    privacyRequestsRequestsEmailRequired,
    privacyRequestsRequestsPhone,
    privacyRequestsRequestsPhoneRequired,
    privacyRequestsRequestDetailsRequired,
    privacyRequestsRequestDetailsTitle,
    required,
  } = useContext(ConfigContext);

  const { hasError, showError, hideError } = useError();

  const baseSchema = object({
    userEmail: pipe(
      string(privacyRequestsRequestsEmailRequired),
      nonEmpty(privacyRequestsRequestsEmailRequired),
      email(privacyRequestsRequestsEmailRequired),
    ),
    userFirstName: optional(string(), ''),
    userLastName: optional(string(), ''),
    userPhoneNumber: union([
      pipe(
        string(),
        regex(
          /^\+?[1-9]\d{0,2}[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
          privacyRequestsRequestsPhoneRequired,
        ),
      ),
      literal(''),
    ]),
    requestDetails: string(),
  });

  const formSchema = detailsRequired
    ? object({
        ...baseSchema.entries,
        requestDetails: pipe(
          string(privacyRequestsRequestDetailsRequired),
          nonEmpty(privacyRequestsRequestDetailsRequired),
        ),
      })
    : object({
        ...baseSchema.entries,
        requestDetails: optional(string()),
      });

  const form = useForm<InferInput<typeof formSchema>>({
    resolver: valibotResolver(formSchema),
    defaultValues: {
      userEmail: '',
      userFirstName: '',
      userLastName: '',
      userPhoneNumber: '',
      requestDetails: '',
    },
  });

  async function submitComplianceRequest(
    values: InferInput<typeof formSchema>,
  ) {
    try {
      setLoading(true);
      const data = await api.compliance.submitComplianceRequest({
        request: requestType,
        userEmail: values.userEmail,
        ...(values.userFirstName && { userFirstName: values.userFirstName }),
        ...(values.userLastName && { userLastName: values.userLastName }),
        ...(values.userPhoneNumber && {
          userPhoneNumber: values.userPhoneNumber,
        }),
        ...(values.requestDetails && { requestDetails: values.requestDetails }),
      });
      hideError();
      setComplianceRequest(data);
      onSubmit();
    } catch (err) {
      handleError(err);
      showError();
      setLoading(false);
    }
  }

  return (
    <Card title={title}>
      <p className="text-muted-foreground mb-5">{description}</p>
      {hasError && (
        <Alert
          icon={<Warning size={18} weight="fill" />}
          subTitle={errorText}
          title={errorTitle}
          variant="error"
        />
      )}
      <Form {...form}>
        <form
          className="text-subheader space-y-5"
          onSubmit={form.handleSubmit(submitComplianceRequest)}
        >
          {detailsRequired && (
            <FormField
              control={form.control}
              name="requestDetails"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {privacyRequestsRequestDetailsTitle}
                    <span className="text-primary">* ({required})</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={placeholder}
                      {...field}
                      data-testid="cd-request-details"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <div className="text-subheader mb-5 grid grid-cols-1 gap-5 md:grid-cols-2">
            <FormField
              control={form.control}
              name="userFirstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{privacyRequestsRequestsFirstName}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={privacyRequestsRequestsFirstName}
                      {...field}
                      data-testid="cd-first-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="userLastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{privacyRequestsRequestsLastName}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={privacyRequestsRequestsLastName}
                      {...field}
                      data-testid="cd-last-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="userEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {privacyRequestsRequestsEmail}
                    <span className="text-primary">* ({required})</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={privacyRequestsRequestsEmail}
                      {...field}
                      data-testid="cd-email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="userPhoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{privacyRequestsRequestsPhone}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={privacyRequestsRequestsPhone}
                      {...field}
                      data-testid="cd-phone"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="mt-4 flex gap-2">
            <div className="flex-grow" />
            <div className="flex gap-2">
              <Button
                data-testid="cd-cancel-request"
                variant="pc-outline"
                onClick={onCancel}
              >
                {consentSettingsCancelButton}
              </Button>
              <Button
                data-testid="cd-submit-request"
                loading={loading}
                type="submit"
                variant="primary"
              >
                {consentSettingsSubmitButton}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Card>
  );
}
